import { Component, Input, Output, EventEmitter, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { PreventionOffersService, OrganizationOfferSummary } from '../../core/services/prevention-offers.service';
import { SessionService } from '../../core/session.service';
import { LanguageService } from '../../core/services/language.service';
import { ToastService } from '../../core/services/toast.service';
import { formatPriceRangesSummary } from '../../core/utils/offer-price.util';
import { PreventionGroupHeaderComponent } from '../../component/prevention-group-header/prevention-group-header.component';
import { ConfirmModalComponent } from '../../component/confirm-modal/confirm-modal.component';
import { lockPageScroll, unlockPageScroll } from '../../layout/shell/shell.component';
import { PreventionGroupService } from '../../core/services/prevention.service';

/** Statut de déploiement (Lot Prevention First UX Phase 1, §5) -- dérivé de `activeSiteCount`
 *  (déjà chargé) vs. le nombre total de sites du groupe, jamais une nouvelle donnée. */
type DeploymentStatus = 'everywhere' | 'partial' | 'none';

/**
 * "Mes Offres" (Lot 9.0-E.E.D) -- UN seul appel `GET /offers` (les agrégats activeSiteCount/
 * priceRanges existent précisément pour éviter un appel availability par carte, Lot 9.0-E.E.B).
 * `session.establishments().length` fournit le nombre total de sites (déjà en mémoire, jamais un
 * nouvel appel, section 7 du lot).
 */
@Component({
  selector: 'app-prevention-my-offers',
  standalone: true,
  imports: [TranslatePipe, PreventionGroupHeaderComponent, ConfirmModalComponent],
  templateUrl: './prevention-my-offers.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./prevention-my-offers.component.scss'],
})
export class PreventionMyOffersComponent {
  /** `true` quand incrusté ailleurs qu'à sa propre route (ex. sous-page Site "Ajouter une offre")
   * -- masque l'en-tête groupe (titre/sous-titre + onglets Vue d'ensemble/Offres & déploiement, qui
   * n'a de sens que sur les 2 pages racines du domaine Groupe Prevention, jamais dans un contexte
   * de navigation qui a déjà son propre fil d'Ariane) ET retire le padding/max-width/centrage propres
   * à cette page (`.my-offers-page`), pour laisser le conteneur hôte (ex. `.satisfaction-subpage` de
   * Site) définir la mise en page -- jamais deux paddings de page superposés. */
  @Input() embedded = false;

  /** N'émettent que si `embedded` -- sinon comportement inchangé (navigation réelle). */
  @Output() createOffer = new EventEmitter<void>();
  @Output() configureOffer = new EventEmitter<{ offerId: string; name: string }>();
  @Output() openLibrary = new EventEmitter<void>();

  private readonly offersSvc = inject(PreventionOffersService);
  private readonly groupSvc = inject(PreventionGroupService);
  private readonly session = inject(SessionService);
  private readonly language = inject(LanguageService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);

  readonly loadError = signal(false);

  /** Incrémenté après un archivage réussi pour recharger la liste depuis le serveur -- `GET /offers`
   *  exclut déjà les offres archivées par défaut (`includeArchived=false`), donc un simple refetch
   *  suffit à faire disparaître la ligne, jamais un filtrage local dupliquant la règle serveur. */
  private readonly refresh = signal(0);

  readonly offers = toSignal(
    toObservable(this.refresh).pipe(
      switchMap(() => this.offersSvc.getOffers().pipe(catchError(() => { this.loadError.set(true); return of(null); })))
    ),
    { initialValue: null as OrganizationOfferSummary[] | null }
  );

  readonly loading = computed(() => this.offers() === null && !this.loadError());
  readonly isEmpty = computed(() => !this.loading() && !this.loadError() && (this.offers()?.length ?? 0) === 0);

  readonly totalSiteCount = computed(() => this.session.establishments().length);

  /**
   * "Sites couverts" (§5 du cadrage) -- réutilise `PreventionGroupService#getDashboard` (déjà
   * consommé par "Vue d'ensemble"), jamais un nouveau calcul : `overview.sitesWithPrevention` est
   * DÉJÀ défini comme "au moins une offre active aujourd'hui", exactement la notion attendue ici.
   * Chargé une seule fois (pas de sélecteur de période sur cette page -- catalogue, pas analytics).
   */
  private readonly groupOverview = toSignal(
    this.groupSvc.getDashboard(30).pipe(catchError(() => of(null))),
    { initialValue: null }
  );

  readonly sitesWithPreventionCount = computed(() => this.groupOverview()?.overview.sitesWithPrevention ?? null);

  /** "Offres actives" (§5) : déployées sur au moins un site. "Offres à déployer" : sur aucun. */
  readonly deployedOffersCount = computed(() => (this.offers() ?? []).filter(o => o.activeSiteCount > 0).length);
  readonly offersToDeployCount = computed(() => (this.offers() ?? []).filter(o => o.activeSiteCount === 0).length);

  /** Badge de statut par carte (§5) -- dérivé de `activeSiteCount` vs. le total de sites, jamais une donnée nouvelle. */
  deploymentStatus(offer: OrganizationOfferSummary): DeploymentStatus {
    const total = this.totalSiteCount();
    if (offer.activeSiteCount === 0) return 'none';
    if (total > 0 && offer.activeSiteCount >= total) return 'everywhere';
    return 'partial';
  }

  /** URL de la vignette de l'offre, ou `null` si elle n'a pas de photo. */
  photoUrl(offer: OrganizationOfferSummary): string | null {
    return offer.hasPhoto ? this.offersSvc.getPhotoUrl(offer.offerId, offer.photoUpdatedAt) : null;
  }

  priceSummary(offer: OrganizationOfferSummary): string {
    return formatPriceRangesSummary(offer.priceRanges, this.language.currentLang(), {
      noActiveSite: this.translate.instant('preventionOffres.myOffers.noActiveSite'),
      variesBySite: this.translate.instant('preventionOffres.myOffers.variesBySite'),
    });
  }

  goCreate(): void {
    if (this.embedded) { this.createOffer.emit(); return; }
    this.router.navigate(['/prevention-offres/nouvelle']);
  }

  goBilans(): void {
    this.router.navigate(['/prevention-offres/bilans']);
  }

  goLibrary(): void {
    if (this.embedded) { this.openLibrary.emit(); return; }
    this.router.navigate(['/prevention-offres/bibliotheque']);
  }

  goConfigure(offerId: string, name: string): void {
    if (this.embedded) { this.configureOffer.emit({ offerId, name }); return; }
    this.router.navigate(['/prevention-offres', offerId]);
  }

  /** Retirer une offre du catalogue du groupe (icône poubelle), même idiome que
   *  `site.component.ts#openRemoveOfferConfirm` côté site -- mais ici l'action réelle est un
   *  archivage (`PUT /offers/{id}/archive`, déjà utilisé par offer-form-page.component.ts), jamais
   *  une suppression physique : l'offre reste consultable/historisée côté backend, seulement
   *  exclue de `GET /offers` (includeArchived=false) et désactivée sur tous les sites où elle était
   *  active. Jamais proposé en mode embedded (sélection d'offre pour un site, pas gestion du
   *  catalogue). */
  readonly archiveTarget = signal<OrganizationOfferSummary | null>(null);
  readonly archiving = signal(false);
  readonly archiveErrorMessage = signal<string | null>(null);

  readonly archiveConfirmMessage = computed(() => {
    const count = this.archiveTarget()?.activeSiteCount ?? 0;
    if (count === 0) return this.translate.instant('preventionOffres.myOffers.archive.confirmMessageNoActiveSite');
    const key = count > 1
      ? 'preventionOffres.myOffers.archive.confirmMessageActiveSitesPlural'
      : 'preventionOffres.myOffers.archive.confirmMessageActiveSitesSingular';
    return this.translate.instant(key, { count });
  });

  openArchiveConfirm(offer: OrganizationOfferSummary, event: Event): void {
    event.stopPropagation();
    this.archiveErrorMessage.set(null);
    this.archiveTarget.set(offer);
    // app-confirm-modal est déclarée dans CE template, donc DESCENDANTE de #w-main (router-outlet)
    // -- inertBackground:false, sinon `inert` sur #w-main rendrait la modale elle-même inerte.
    lockPageScroll({ inertBackground: false });
  }

  closeArchiveConfirm(): void {
    if (this.archiving()) return;
    this.archiveTarget.set(null);
    unlockPageScroll();
  }

  confirmArchive(): void {
    const offer = this.archiveTarget();
    if (!offer || this.archiving()) return;
    this.archiving.set(true);
    this.archiveErrorMessage.set(null);
    this.offersSvc.archiveOffer(offer.offerId).subscribe({
      next: () => {
        this.archiving.set(false);
        this.archiveTarget.set(null);
        unlockPageScroll();
        this.refresh.update(n => n + 1);
        this.toast.show(
          this.translate.instant('preventionOffres.offerForm.toasts.archived'),
          'success',
          3000,
          this.translate.instant('preventionOffres.offerForm.toasts.archivedSubtitle')
        );
      },
      error: (err) => {
        this.archiving.set(false);
        const message = (err as { error?: { message?: string } })?.error?.message
          ?? this.translate.instant('preventionOffres.offerForm.errors.archiveFailed');
        this.archiveErrorMessage.set(message);
      },
    });
  }
}
